import { Field } from "@/components/atoms/Field"
import type { EnrollmentResponse } from "@/types/enrollment"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Flex,
  Input,
  Portal,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react"
import { memo, useEffect } from "react"
import { useForm } from "react-hook-form"

interface RecordPaymentFormData {
  amount_paid: number
  payment_method: string
  payment_date: string
  notes: string
  external_payment_id?: string
}

interface RecordPaymentModalProps {
  isOpen: boolean
  enrollment: EnrollmentResponse | null
  onClose: () => void
  onSubmit: (enrollmentId: string, data: RecordPaymentFormData) => Promise<void>
  isSubmitting?: boolean
}

const RecordPaymentModal = memo(
  ({
    isOpen,
    enrollment,
    onClose,
    onSubmit,
    isSubmitting = false,
  }: RecordPaymentModalProps) => {
    const form = useForm<RecordPaymentFormData>({
      defaultValues: {
        amount_paid: 0,
        payment_method: "",
        payment_date: new Date().toISOString().slice(0, 16), // datetime-local format
        external_payment_id: "",
        notes: "",
      },
    })

    // Calculate payment details from nested invoice
    const amountDue = enrollment?.invoice?.amount_due ?? 0
    const currentlyPaid = enrollment?.invoice?.amount_paid ?? 0
    const outstandingBalance = amountDue - currentlyPaid

    // Format currency
    const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`

    // Watch the payment amount to show preview
    const paymentAmount = form.watch("amount_paid")
    const newBalance = outstandingBalance - (paymentAmount || 0)

    useEffect(() => {
      if (isOpen && enrollment) {
        // Pre-fill with outstanding balance
        form.reset({
          amount_paid: outstandingBalance,
          payment_method: "",
          payment_date: new Date().toISOString().slice(0, 16),
          external_payment_id: "",
          notes: "",
        })
      }
    }, [isOpen, enrollment, outstandingBalance, form])

    const handleSubmit = form.handleSubmit(async (data) => {
      if (!enrollment) return
      await onSubmit(enrollment.id, data)
      form.reset()
    })

    if (!enrollment) return null

    return (
      <Portal>
        <DialogRoot
          open={isOpen}
          onOpenChange={(e) => !e.open && onClose()}
          size={{ base: "full", md: "md" }}
        >
          <DialogBackdrop />
          <DialogPositioner>
            <DialogContent maxH="90vh" display="flex" flexDirection="column">
              <DialogHeader>
                <DialogTitle>Record Payment</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody overflowY="auto" flex="1">
                {/* Payment Summary */}
                <Box
                  p={3}
                  mb={4}
                  bg="blue.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="blue.200"
                >
                  <Stack gap={2} fontSize="sm">
                    <Flex justify="space-between">
                      <Text fontWeight="medium">Total Amount Due:</Text>
                      <Text fontWeight="bold">{formatCurrency(amountDue)}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontWeight="medium">Currently Paid:</Text>
                      <Text fontWeight="bold" color="green.600">
                        {formatCurrency(currentlyPaid)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" pt={2} borderTopWidth="1px">
                      <Text fontWeight="bold" color="orange.700">
                        Outstanding Balance:
                      </Text>
                      <Text fontWeight="bold" fontSize="lg" color="orange.700">
                        {formatCurrency(outstandingBalance)}
                      </Text>
                    </Flex>
                  </Stack>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Stack gap={4}>
                    <Field
                      label="Payment Amount"
                      required
                      invalid={!!form.formState.errors.amount_paid}
                      errorText={form.formState.errors.amount_paid?.message}
                    >
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max={outstandingBalance}
                        placeholder="Enter payment amount"
                        {...form.register("amount_paid", {
                          valueAsNumber: true,
                          required: "Payment amount is required",
                          min: {
                            value: 0.01,
                            message: "Amount must be greater than $0",
                          },
                          max: {
                            value: outstandingBalance,
                            message: `Amount cannot exceed outstanding balance of ${formatCurrency(outstandingBalance)}`,
                          },
                        })}
                      />
                    </Field>

                    {/* Payment Preview */}
                    {paymentAmount > 0 && (
                      <Box
                        p={3}
                        bg={newBalance <= 0 ? "green.50" : "yellow.50"}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={
                          newBalance <= 0 ? "green.200" : "yellow.200"
                        }
                      >
                        <Stack gap={1} fontSize="sm">
                          <Flex justify="space-between">
                            <Text fontWeight="medium">New Balance:</Text>
                            <Text
                              fontWeight="bold"
                              fontSize="lg"
                              color={
                                newBalance <= 0 ? "green.600" : "orange.600"
                              }
                            >
                              {formatCurrency(newBalance)}
                            </Text>
                          </Flex>
                          <Text fontSize="xs" color="fg.subtle">
                            {newBalance <= 0
                              ? "✓ Enrollment will be marked as PAID"
                              : "Enrollment will be marked as PARTIAL"}
                          </Text>
                        </Stack>
                      </Box>
                    )}

                    <Field
                      label="Payment Method"
                      required
                      invalid={!!form.formState.errors.payment_method}
                      errorText={form.formState.errors.payment_method?.message}
                    >
                      <select
                        {...form.register("payment_method", {
                          required: "Payment method is required",
                        })}
                        className="chakra-select"
                      >
                        <option value="">Select payment method</option>
                        <option value="cash">Cash</option>
                        <option value="check">Check</option>
                        <option value="bank_transfer">Zelle</option>
                        <option value="credit_card">Credit Card</option>
                        <option value="debit_card">Debit Card</option>
                        <option value="other">Other</option>
                      </select>
                    </Field>

                    <Field
                      label="Payment Date"
                      required
                      invalid={!!form.formState.errors.payment_date}
                      errorText={form.formState.errors.payment_date?.message}
                    >
                      <Input
                        type="datetime-local"
                        {...form.register("payment_date", {
                          required: "Payment date is required",
                        })}
                      />
                    </Field>

                    <Field
                      label="Payment Reference ID"
                      helperText="Optional: Zelle confirmation, check number, or transaction ID"
                    >
                      <Input
                        type="text"
                        placeholder="e.g., ABC123, Check #1234"
                        {...form.register("external_payment_id")}
                      />
                    </Field>

                    <Field
                      label="Notes"
                      helperText="Optional: Additional notes about this payment"
                    >
                      <Textarea
                        placeholder="e.g., Paid in full for Fall 2025 session"
                        {...form.register("notes")}
                      />
                    </Field>
                  </Stack>
                </form>
              </DialogBody>

              <DialogFooter>
                <Button variant="outline" onClick={onClose} mr={3}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  loadingText="Recording Payment..."
                >
                  Record Payment
                </Button>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </DialogRoot>
      </Portal>
    )
  },
)

RecordPaymentModal.displayName = "RecordPaymentModal"

export default RecordPaymentModal
